import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Clock,
  Info,
  PackageOpen,
  Receipt,
  ShoppingCart,
  Sprout,
  Truck,
  XCircle,
} from "lucide-react";
import type { NotificationDto, NotificationType } from "../services/notificationsApi";

const ICON_BY_TYPE: Record<NotificationType, typeof Bell> = {
  DiscardApprovalRequested: AlertTriangle,
  DiscardApproved: CheckCircle2,
  DiscardRejected: XCircle,
  SowingInstructionRejected: XCircle,
  SowingInstructionAmendmentRequested: AlertTriangle,
  SowingInstructionAmendmentApproved: CheckCircle2,
  SowingInstructionAmendmentRejected: XCircle,
  SowingWindowReminder: Clock,
  PlannedSowingReminder: Sprout,
  PlannedSowingOverdue: AlertTriangle,
  GerminationOverdue: AlertTriangle,
  DailySowingPlan: Info,
  WeeklySowingPlan: Info,
  StockDepletionAlert: PackageOpen,
  StockReceiptPendingApproval: AlertTriangle,
  StockReceiptApproved: CheckCircle2,
  StockReceiptRejected: XCircle,
  CustomerSeedReceiptPendingApproval: AlertTriangle,
  CustomerSeedReceiptApproved: CheckCircle2,
  CustomerSeedReceiptRejected: XCircle,
  SupplierCreationPendingApproval: AlertTriangle,
  SupplierCreationApproved: CheckCircle2,
  SupplierCreationRejected: XCircle,
  OrderPartialFulfillment: Truck,
  WalkInSaleCompleted: ShoppingCart,
  ReceiptReuseRejected: Receipt,
  PaymentPendingApproval: AlertTriangle,
  PaymentApproved: CheckCircle2,
  PaymentRejected: XCircle,
  BroadcastMessage: Bell,
};

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime();
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86_400) return `${Math.floor(diffSec / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export function NotificationRow({
  notification,
  onClick,
}: {
  notification: NotificationDto;
  onClick: (n: NotificationDto) => void;
}) {
  const Icon = ICON_BY_TYPE[notification.type] ?? Bell;
  const isAction = notification.category === "ActionRequired";

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className={`flex w-full gap-3 px-3 py-2.5 text-left transition hover:bg-accent/60 ${
        notification.isRead ? "opacity-70" : ""
      }`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isAction ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className={`truncate text-sm ${notification.isRead ? "font-normal" : "font-medium"}`}>
            {notification.title}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatRelative(notification.createdAt)}
          </span>
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{notification.body}</p>
      </div>
      {!notification.isRead && (
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="unread" />
      )}
    </button>
  );
}
